<script setup>
import { computed } from 'vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: 'Detalhe da entrega' },
  entry: { type: Object, default: null },
  canReplay: { type: Boolean, default: false },
  replayLabel: { type: String, default: 'Reenviar' },
  redactedKeys: { type: Array, default: () => [] },
  destinationLabel: { type: String, default: 'Destino' },
  providerLabel: { type: String, default: 'Provedor' },
  idempotencyLabel: { type: String, default: 'Idempotência' },
  receiptLabel: { type: String, default: 'Recibo' },
  attemptsLabel: { type: String, default: 'Tentativas' },
  payloadLabel: { type: String, default: 'Payload' },
  emptyLabel: { type: String, default: 'Sem detalhes disponíveis' },
});

const emit = defineEmits(['close', 'replay']);

/**
 * LaisLedgerDrawer — detail drawer for a single delivery/payment/key/webhook/
 * WhatsApp ledger entry: status, provider/destination, attempt history,
 * receipt, redacted payload, error and an optional retry/replay affordance.
 *
 * Presentational only: the domain owns idempotency and the replay call; this
 * component just surfaces the state and emits `replay`.
 *
 * entry: {
 *   status, provider, destination, idempotencyKey, receipt, error,
 *   payload, attempts: [{ at, status, error }]
 * }
 */
const STATUS_TONE = Object.freeze({
  delivered: 'positive',
  sent: 'positive',
  paid: 'positive',
  success: 'positive',
  retrying: 'info',
  pending: 'warning',
  queued: 'info',
  bounce: 'warning',
  bounced: 'warning',
  failed: 'danger',
  error: 'danger',
});

const statusTone = status =>
  STATUS_TONE[String(status || '').toLowerCase()] || 'neutral';

const rows = computed(() => {
  const entry = props.entry || {};
  return [
    { key: 'provider', label: props.providerLabel, value: entry.provider },
    {
      key: 'destination',
      label: props.destinationLabel,
      value: entry.destination,
    },
    {
      key: 'idempotency',
      label: props.idempotencyLabel,
      value: entry.idempotencyKey,
      mono: true,
    },
    {
      key: 'receipt',
      label: props.receiptLabel,
      value: entry.receipt,
      mono: true,
    },
  ].filter(row => row.value);
});

const attempts = computed(() =>
  Array.isArray(props.entry?.attempts) ? props.entry.attempts : []
);

const formatAt = value => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};
</script>

<template>
  <LaisDrawer
    :show="show"
    :title="title"
    side="right"
    size="md"
    @close="emit('close')"
  >
    <template v-if="entry">
      <div class="flex flex-col gap-5">
        <div class="flex items-center gap-2">
          <LaisStatusBadge
            :label="entry.status || 'unknown'"
            :tone="statusTone(entry.status)"
            dot
          />
        </div>

        <dl v-if="rows.length" class="grid gap-3">
          <div v-for="row in rows" :key="row.key" class="flex flex-col gap-0.5">
            <dt class="text-lais-label-xs uppercase text-lais-text-muted">
              {{ row.label }}
            </dt>
            <dd
              class="break-words text-lais-body-sm text-lais-text"
              :class="row.mono ? 'font-mono text-lais-body-xs' : ''"
            >
              {{ row.value }}
            </dd>
          </div>
        </dl>

        <div
          v-if="entry.error"
          role="alert"
          class="rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-2.5 text-lais-body-sm text-lais-danger"
        >
          {{ entry.error }}
        </div>

        <section v-if="attempts.length" class="flex flex-col gap-2">
          <h3
            class="text-lais-label-xs font-semibold uppercase text-lais-text-muted"
          >
            {{ attemptsLabel }}
          </h3>
          <ul role="list" class="grid gap-2">
            <li
              v-for="(attempt, index) in attempts"
              :key="index"
              class="flex items-center justify-between gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2"
            >
              <div class="flex items-center gap-2">
                <LaisStatusBadge
                  :label="attempt.status || 'unknown'"
                  :tone="statusTone(attempt.status)"
                  size="sm"
                />
                <span
                  v-if="attempt.error"
                  class="truncate text-lais-body-xs text-lais-text-muted"
                >
                  {{ attempt.error }}
                </span>
              </div>
              <time
                v-if="formatAt(attempt.at)"
                class="shrink-0 text-lais-label-xs tabular-nums text-lais-text-muted"
              >
                {{ formatAt(attempt.at) }}
              </time>
            </li>
          </ul>
        </section>

        <section v-if="entry.payload" class="flex flex-col gap-2">
          <LaisPayloadViewer
            :payload="entry.payload"
            :title="payloadLabel"
            :redacted-keys="redactedKeys"
          />
        </section>
      </div>
    </template>

    <p
      v-else
      class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-lais-body-sm text-lais-text-muted"
    >
      {{ emptyLabel }}
    </p>

    <template v-if="entry && canReplay" #footer>
      <div class="flex justify-end">
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais bg-lais-primary px-4 text-lais-body-sm font-semibold text-white transition hover:bg-lais-primary-600 focus-visible:outline-none focus-visible:shadow-lais-focus"
          @click="emit('replay', entry)"
        >
          <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
          {{ replayLabel }}
        </button>
      </div>
    </template>
  </LaisDrawer>
</template>
